import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function TargetHRCalc() {
  const [source, setSource] = useState('age'); // age | test
  const [age, setAge] = useState(30);
  const [testHR, setTestHR] = useState(190);
  const [rest, setRest] = useState(70);
  const [formula, setFormula] = useState('karvonen'); // karvonen | tanaka | gulati
  const clear = () => { setSource('age'); setAge(30); setTestHR(190); setRest(70); setFormula('karvonen'); };

  const c = useMemo(() => {
    let maxHR;
    if (source === 'test') maxHR = +testHR || 0;
    else if (formula === 'tanaka') maxHR = 208 - 0.7 * age;
    else if (formula === 'gulati') maxHR = 206 - 0.88 * age;
    else maxHR = 220 - age;
    const reserve = maxHR - (+rest || 0);
    const zones = [
      { name: 'Warm-up / Cool-down', lo: 50, hi: 60 },
      { name: 'Fat Burn (light)', lo: 60, hi: 70 },
      { name: 'Aerobic (moderate)', lo: 70, hi: 80 },
      { name: 'Anaerobic (hard)', lo: 80, hi: 90 },
      { name: 'Max (VO₂ max)', lo: 90, hi: 100 },
    ];
    return { maxHR, reserve, zones };
  }, [source, age, testHR, rest, formula]);

  return (
    <CalcShell look="cnet" title="Target Heart Rate Calculator" subtitle="Compute training heart-rate zones using the Karvonen (heart rate reserve) method.">
      <CalcNetPanel
        slug="thr"
        tabs={['Target HR']}
        activeTab={0}
        onTabChange={() => {}}
        onClear={clear}
        settings={
          <>
            <Row label="Max HR Formula">
              <select value={formula} onChange={e => setFormula(e.target.value)} data-testid="thr-formula"
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="karvonen">220 − Age (classic)</option>
                <option value="tanaka">Tanaka: 208 − 0.7·Age</option>
                <option value="gulati">Gulati (women): 206 − 0.88·Age</option>
                <option value="nes">Nes: 211 − 0.64·Age</option>
                <option value="oakland">Oakland (nonlinear): 192 − 0.007·Age²</option>
              </select>
            </Row>
            <Row label="Fitness Level">
              <select data-testid="thr-fitness" className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option>Beginner</option><option>Intermediate</option><option>Advanced</option><option>Elite / Athlete</option>
              </select>
            </Row>
          </>
        }
        right={
          <div>
            <div className="text-lg text-slate-800">Maximum Heart Rate</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="thr-max">{Math.round(c.maxHR)} bpm</div>
            <div className="text-xs text-slate-600 mt-1">Heart Rate Reserve: <b>{Math.round(c.reserve)}</b> bpm</div>
            <div className="mt-3">
              <h4 className="font-bold text-slate-900 mb-2 text-sm">Target Heart Rate Zones</h4>
              <table className="w-full text-sm border border-slate-300" data-testid="thr-zones-color-table">
                <thead className="bg-slate-100">
                  <tr><th className="px-3 py-2 text-left">Zone</th><th className="px-3 py-2 text-left">Intensity</th><th className="px-3 py-2 text-right">Target HR</th></tr>
                </thead>
                <tbody>
                  {c.zones.map((z, i) => {
                    const rowBg = { 'Warm Up': 'bg-blue-100', 'Fat Burn': 'bg-emerald-100', 'Aerobic': 'bg-yellow-100', 'Anaerobic': 'bg-orange-100', 'Maximum': 'bg-red-200' }[z.name] || 'bg-slate-50';
                    return (
                      <tr key={z.name} className={rowBg}>
                        <td className="px-3 py-1.5 font-semibold">{z.name}</td>
                        <td className="px-3 py-1.5 text-xs">{z.lo}–{z.hi}% HRR</td>
                        <td className="px-3 py-1.5 text-right font-mono">{Math.round((+rest || 0) + z.lo / 100 * c.reserve)} – {Math.round((+rest || 0) + z.hi / 100 * c.reserve)} bpm</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        }
      >
        <Row label="Max Heart Rate">
          <div className="flex flex-col gap-2 flex-1">
            <RadioGroup name="thr-src" value={source} onChange={setSource}
              options={[{value:'age',label:'Estimate from age'}, {value:'test',label:'Test result'}]} />
            {source === 'age'
              ? <UnitInput value={age} onChange={setAge} testid="thr-age" />
              : <UnitInput value={testHR} onChange={setTestHR} unit="bpm" testid="thr-test" />}
          </div>
        </Row>
        <Row label="Resting Heart Rate"><UnitInput value={rest} onChange={setRest} unit="bpm" testid="thr-rest" /><span className="text-slate-500 text-xs">(optional)</span></Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Max HR = ${formula==='tanaka'?'208 − 0.7·Age':formula==='gulati'?'206 − 0.88·Age':'220 − Age'} = <b>${Math.round(c.maxHR)}</b>`,
        `Heart Rate Reserve = Max − Rest = ${Math.round(c.maxHR)} − ${rest} = <b>${Math.round(c.reserve)}</b>`,
        `Target HR = Rest + intensity × Reserve  (Karvonen)`,
      ]} />
    </CalcShell>
  );
}
